import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { Layout } from '@/components/layout/Layout';
import { Users, Utensils, Briefcase, ChevronRight, Target, MessageSquare, Handshake, UserCheck } from 'lucide-react';

const HERO_IMAGE = 'https://customer-assets.emergentagent.com/job_parla-italiano-268/artifacts/vu9xsrff_foto%20mani%20pizza.png';
const FOUNDER_IMAGE = 'https://customer-assets.emergentagent.com/job_parla-italiano-268/artifacts/e7q11rhz_foto%20mia%20pizzaiolo%20sfondo.JPG';
const SERVICES_BG = 'https://images.unsplash.com/photo-1556745757-8d76bdb6984b?w=1920&q=80';

export default function HomePage() {
  const { t } = useLanguage();

  const services = [
    {
      icon: Target,
      title: t.services.matching.title,
      desc: t.services.matching.desc
    },
    {
      icon: Users,
      title: t.services.mentoring.title,
      desc: t.services.mentoring.desc
    },
    {
      icon: Briefcase,
      title: t.services.consulting.title,
      desc: t.services.consulting.desc
    }
  ];

  const steps = [
    { icon: UserCheck, ...t.howItWorks.step1 },
    { icon: MessageSquare, ...t.howItWorks.step2 },
    { icon: Target, ...t.howItWorks.step3 },
    { icon: Handshake, ...t.howItWorks.step4 }
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center" data-testid="hero-section">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src={HERO_IMAGE}
            alt="Pizza artisan hands"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/90 to-background/60" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-background/40" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-24">
          <div className="max-w-2xl">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-serif leading-tight mb-6 animate-fade-in-up">
              <span className="text-foreground">{t.hero.title}</span>
              <br />
              <span className="text-gradient-gold">{t.hero.titleAccent}</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-10 animate-fade-in-up animation-delay-200">
              {t.hero.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up animation-delay-300">
              <Link
                to="/per-pizzaioli"
                className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground font-semibold uppercase tracking-widest px-8 py-4 rounded-sm btn-glow hover:brightness-110 transition-all duration-300"
                data-testid="cta-chef"
              >
                {t.hero.ctaChef}
                <ChevronRight size={18} />
              </Link>
              <Link
                to="/per-ristoratori"
                className="inline-flex items-center justify-center gap-2 bg-transparent border border-primary/40 text-primary font-semibold uppercase tracking-widest px-8 py-4 rounded-sm hover:bg-primary/10 transition-all duration-300"
                data-testid="cta-restaurant"
              >
                {t.hero.ctaRestaurant}
                <ChevronRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 md:py-32 relative" data-testid="services-section">
        <div 
          className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: 'url("https://static.prod-images.emergentagent.com/jobs/d9cf0590-fa7d-4aa8-865e-545c02b78803/images/8eabe309ffeb509bb397958a21f919dd9228677ca7ac7726807c0a3f5769e439.png")' }}
        />
        <div className="absolute inset-0 z-0 bg-black/70" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-serif text-foreground mb-4">
              {t.services.title}
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              {t.services.subtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, idx) => (
              <div
                key={idx}
                className="bg-background border border-primary/10 hover:border-primary/30 p-8 rounded-sm card-hover"
                style={{ animationDelay: `${idx * 100}ms` }}
                data-testid={`service-card-${idx}`}
              >
                <div className="w-14 h-14 rounded-sm bg-primary/10 flex items-center justify-center mb-6">
                  <service.icon className="text-primary" size={28} />
                </div>
                <h3 className="text-xl font-serif text-foreground mb-3">
                  {service.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  {service.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 md:py-32" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Image */}
            <div className="relative">
              <div className="aspect-[4/5] rounded-sm overflow-hidden border border-primary/20">
                <img
                  src={FOUNDER_IMAGE}
                  alt="Giacomo - Fondatore"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-primary/10 rounded-sm -z-10" />
            </div>

            {/* Content */}
            <div>
              <span className="text-primary uppercase tracking-widest text-sm font-medium mb-2 block">
                {t.about.subtitle}
              </span>
              <h2 className="text-3xl md:text-5xl font-serif text-foreground mb-6">
                {t.about.title}
              </h2>
              <p className="text-foreground text-lg mb-4">
                {t.about.intro}
              </p>
              <p className="text-muted-foreground leading-relaxed mb-6">
                {t.about.experience}
              </p>
              <p className="text-muted-foreground leading-relaxed mb-8 italic border-l-2 border-primary pl-4">
                "{t.about.mission}"
              </p>
              <Link
                to="/chi-siamo"
                className="inline-flex items-center gap-2 text-primary font-medium hover:gap-3 transition-all duration-300"
                data-testid="about-cta"
              >
                {t.about.cta}
                <ChevronRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-24 md:py-32 bg-card relative noise-overlay" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-serif text-foreground mb-4">
              {t.howItWorks.title}
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              {t.howItWorks.subtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, idx) => (
              <div key={idx} className="relative" data-testid={`step-${idx + 1}`}>
                {/* Connector Line */}
                {idx < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-8 left-[60%] w-full h-[2px] bg-primary/20" />
                )}
                
                <div className="relative z-10 text-center">
                  <div className="w-16 h-16 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center mx-auto mb-6">
                    <span className="text-primary font-serif text-2xl">{idx + 1}</span>
                  </div>
                  <h3 className="text-xl font-serif text-foreground mb-3">
                    {step.title}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {step.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl md:text-5xl font-serif text-foreground mb-6">
            {t.hero.title} <span className="text-gradient-gold">{t.hero.titleAccent}</span>
          </h2>
          <p className="text-muted-foreground text-lg mb-10 max-w-2xl mx-auto">
            {t.hero.subtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/per-pizzaioli"
              className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground font-semibold uppercase tracking-widest px-8 py-4 rounded-sm btn-glow hover:brightness-110 transition-all duration-300"
            >
              {t.hero.ctaChef}
              <ChevronRight size={18} />
            </Link>
            <Link
              to="/per-ristoratori"
              className="inline-flex items-center justify-center gap-2 bg-transparent border border-primary/40 text-primary font-semibold uppercase tracking-widest px-8 py-4 rounded-sm hover:bg-primary/10 transition-all duration-300"
            >
              {t.hero.ctaRestaurant}
              <ChevronRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
